import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getAvailableDurationPackageOptions,
  getDurationPackageOptions,
} from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { generateAccommodationCheckoutItems, generateVillaCheckoutItem } from 'lib/checkout/accommodation/cart'
import { generateExperiencesPassportCheckoutItems } from 'lib/checkout/experiences/cart'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import GeoContext from 'contexts/geoContext'
import { createLEHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/leHotel'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getAvailableRoomsByPackage, getUpgradeablePackage, mapClientOfferToServerOffer, mapClientPackageToServerPackage, mapRoomOccupantsToServerOccuppancies } from '../utils'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { isFlashHotelOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { getUpgradeItemContext, upgradeEvent } from 'analytics/snowplow/events'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PackageUpgradeModal from '../PackageUpgrade/PackageUpgradeModal'
import moment from 'moment'
import { checkoutStateIsReady } from 'middlewares/analyticsMiddleware'
import getSelectedLoyaltyUpgrade from '../offerPageUtils/getSelectedLoyaltyTargetUpgradePackage'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getUpgradeablePackage as getUpgradeablePackageLib } from '@luxuryescapes/lib-room-upgrade'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import CurrencyContext from 'contexts/currencyContext'
import buildPrefetchTopOfferFilters, { isExperienceListStale } from 'lib/experiences/buildPrefetchTopOfferFilters'
import { fetchExperienceList, type ExperienceListFilters } from 'actions/ExperienceActions'
import { shouldPrefetchExperiencesInFlow, isOneClickExperienceTransferEnabled } from 'checkout/selectors/experienceSelectors'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { buildOfferPageCartAttributes, DEFAULT_FUNNEL_FEATURES } from 'checkout/selectors/funnel'
import { refreshFunnelOptimiserFlags } from 'actions/SystemActions'
import { useSeatEligiblePassengers } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { getRoundTripSeatsLeft, hasInsufficientCharterSeats } from 'lib/charterFlights/charterFlightAvailability'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useIntl } from 'react-intl'
import useExperiment from 'hooks/Experiments/useExperiment'
import {
  isInternationalCheckoutFlowEligibleOfferType,
  isInternationalCheckoutFlowEligibleRegion,
  isInternationalCheckoutFlowEligibleUser,
} from 'lib/checkout/internationalCheckoutFlow'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isSpoofed } from 'selectors/isSpoofed'
import useExperiencesPassportForOffer from 'components/Experiences/ExperiencesPassport/useExperiencesPassportForOffer'

interface GenerateCartOptions {
  packageOption: App.OfferPackageOptionView | undefined
  flights: boolean
  upgradePackageOption?: App.OfferPackageOptionView
  originalPackageOption?: App.OfferPackageOptionView
  loyaltyAppliedUpgradeType?: App.LoyaltyStayUpgradeType
  basePackageId?: string
}

interface Props extends ComponentProps<typeof TextButton> {
  airport?: App.Airport
  checkIn?: string
  checkOut?: string
  duration?: number
  isLoggedIn: boolean
  offer: App.HotelOffer | App.VillaOffer
  packageOptions: Array<App.OfferPackageOptionView>
  availablePackageOptions: Array<App.OfferPackageOptionView>
  packageView: App.OfferPackageOptionView
  rooms: Array<App.RoomOccupants>
  onSubmit?: (e: React.MouseEvent<HTMLButtonElement>) => void
  tripPlannerTripId?: string
  reserveForZero?: boolean
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>) => void
  upgradeEnabled?: boolean
  loyaltyTargetRoomUpgradesSelected?: App.LoyaltyTargetStayUpgradesSelected
  isPackageUpgradeLibEnabled: boolean
  disablePrefetchExperiences?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FareClassWithReward
  bypassRegionFilter?: boolean
  bypassRegionSettingId?: string
}

function OfferCheckoutButton(props: Props) {
  const {
    children,
    packageView,
    rooms,
    isLoggedIn,
    checkIn,
    checkOut,
    duration,
    packageOptions,
    availablePackageOptions,
    offer,
    airport,
    onSubmit,
    tripPlannerTripId,
    onProceedToBooking,
    upgradeEnabled = true,
    loyaltyTargetRoomUpgradesSelected,
    isPackageUpgradeLibEnabled,
    disablePrefetchExperiences = false,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    disabled,
    bypassRegionFilter,
    bypassRegionSettingId,
    ...rest
  } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const dispatch = useAppDispatch()
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const { currentRegionCode } = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { isAnyVariant: prefetchEnabled, triggerActivation: triggerExperiencesAnalytics } = useExperiment(Experiments.croPrefetchInflowCheckoutExperiences, { canActivate: false })
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const numberOfPurchases = useAppSelector(state => state.auth.account.numberOfPurchases)
  const spoofed = useAppSelector(isSpoofed)
  const canActivateInternationalCheckoutFlow = isInternationalCheckoutFlowEligibleRegion(currentRegionCode) &&
    isInternationalCheckoutFlowEligibleOfferType(offer.type) &&
    isInternationalCheckoutFlowEligibleUser({ canRedeemLuxPlusBenefits, numberOfPurchases, isSpoofed: spoofed })
  const { isAnyVariant: isInternationalCheckoutFlowBucketed } = useExperiment(
    Experiments.internationalCheckoutFlow,
    {
      canActivate: canActivateInternationalCheckoutFlow,
    },
  )
  const isInternationalCheckoutFlowTreatment = canActivateInternationalCheckoutFlow &&
    isInternationalCheckoutFlowBucketed
  const canPrefetchInFlow = useAppSelector(state => shouldPrefetchExperiencesInFlow(state, offer, disablePrefetchExperiences))
  const experienceLists = useAppSelector(state => state.experience.experienceLists)
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const geoCoords = useMemo(() => ({ latitude: offer?.property?.latitude, longitude: offer?.property?.longitude }), [offer?.property?.latitude, offer?.property?.longitude])
  const oneClickTransferEnabled = useAppSelector(state => isOneClickExperienceTransferEnabled(state))
  // We are passed a package view, but that's not always guaranteed to be the 'latest' one
  // e.g. a `selectedPackageView` doesn't include price updates based on date selection (maybe there was a surcharge?)
  // So to get around this, we always use the 'latest' calculated view for the unique key we're given
  const latestPackageView = useMemo(() => packageOptions.find(option => option.package.uniqueKey === packageView.package.uniqueKey), [packageOptions, packageView.package.uniqueKey])
  const {
    checkoutSources: experiencesPassportCheckoutSources,
    fetching: experiencesPassportFetching,
  } = useExperiencesPassportForOffer()

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const effectiveCharterBundleRoute = useMemo(
    () => (charterBundleEnabled ? charterBundleRoute : undefined),
    [charterBundleEnabled, charterBundleRoute],
  )
  const baseFareClass = toBaseFareClass(fareClass)

  const {
    departingJourney: departingCharterFlightJourney,
    returningJourney: returningCharterFlightJourney,
    departingFareFamily: departingCharterFareFamily,
    returningFareFamily: returningCharterFareFamily,
    searchResultsTimestamp: charterFlightSearchResultsTimestamp,
    providerSearchId: charterFlightSearchId,
    fetching: charterFlightsFetching,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: effectiveCharterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass: baseFareClass,
    feederOriginAirportCode: airport?.code,
  })

  const awaitingCharterBundleData = !!effectiveCharterBundleRoute && charterFlightsFetching

  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const charterSeatsLeft = useMemo(() => getRoundTripSeatsLeft(departingCharterFlightJourney, returningCharterFlightJourney), [departingCharterFlightJourney, returningCharterFlightJourney])
  const insufficientCharterSeats = !!effectiveCharterBundleRoute && hasInsufficientCharterSeats(checkIn, checkOut, duration, charterSeatsLeft, seatEligiblePassengers)

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: duration,
    charterBundleRoute: effectiveCharterBundleRoute,
    rooms,
    fareClass: baseFareClass,
    feederOriginAirportCode: airport?.code,
  })

  // For charter bundle, check if checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !effectiveCharterBundleRoute || (!!checkIn && !!charterDepartDate)

  const generateCart = useCallback(({
    packageOption,
    flights,
    upgradePackageOption,
    originalPackageOption,
    loyaltyAppliedUpgradeType,
    basePackageId,
  }: GenerateCartOptions) => {
    if (!packageOption || !checkIn) { return }

    const checkOut = moment(checkIn).add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT)
    const dates = { checkIn, checkOut }

    let accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem> = []
    if (offer.type !== 'rental') {
      accommodationItems = generateAccommodationCheckoutItems({
        offer,
        packageOption,
        dates,
        roomOccupants: rooms,
        currencyCode: currentCurrency,
        loyaltyAppliedUpgradeType,
        basePackageId,
        bypassRegionFilter,
        bypassRegionSettingId,
      })
    }

    let villaItems: Array<App.Checkout.VillaItem> = []
    if (offer.type === 'rental') {
      villaItems = [generateVillaCheckoutItem(offer, packageOption, dates, rooms[0], currentCurrency)]
    }

    const flightItems = flights && airport ? generateAccommodationBundleFlightItems({
      rooms,
      airport,
      offer,
      accommodationItems,
      quotedFare: flightPrice,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney,
      returningCharterFlightJourney,
      departingCharterFareFamily,
      returningCharterFareFamily,
      charterFlightSearchResultsTimestamp,
      charterFlightSearchId,
    }) : []

    const experiencesPassportItems = generateExperiencesPassportCheckoutItems(
      accommodationItems,
      experiencesPassportCheckoutSources,
    )

    if (upgradePackageOption && originalPackageOption && accommodationItems.length === 1) {
      checkoutStateIsReady().then(() => trackEvent(
        upgradeEvent({
          hasUpgraded: packageOption.package.uniqueKey === upgradePackageOption.package.uniqueKey,
          chosenCartItemId: accommodationItems[0].itemId,
        },
        getUpgradeItemContext(originalPackageOption, upgradePackageOption, accommodationItems[0]),
        ['hotel'],
        ),
      ),
      )
    }
    onProceedToBooking([...accommodationItems, ...villaItems, ...flightItems, ...experiencesPassportItems])
  }, [checkIn, offer, airport, rooms, flightPrice, charterBundleRoute, fareClass, departingCharterFlightJourney, returningCharterFlightJourney, departingCharterFareFamily, returningCharterFareFamily, charterFlightSearchResultsTimestamp, charterFlightSearchId, experiencesPassportCheckoutSources, onProceedToBooking, currentCurrency, bypassRegionFilter, bypassRegionSettingId])

  const onBookNow = useCallback(async(packageOption: App.OfferPackageOptionView, flights: boolean) => {
    // we're booking, close the filters
    offerPageDispatch({ type: OfferPageActions.closeFilter })
    // also, if we're currently being called from *inside* a modal - close it
    modalElementContext?.resolve()

    // Refresh funnel optimiser flags with cart attributes and UTMs before upgrade modal decision
    const cartAttributes = buildOfferPageCartAttributes({
      totalPrice: flights ? packageOption.hotelPrice + packageOption.flightPrice : packageOption.hotelPrice,
      destinationCountry: offer.property?.geoData?.country,
      rooms,
      checkIn,
      duration: packageOption.package.duration,
      offerId: offer.id,
      productType: 'hotel',
    })
    const currentFunnelFeatures = (await dispatch(refreshFunnelOptimiserFlags(cartAttributes))) ?? DEFAULT_FUNNEL_FEATURES

    const saveToCurrentTrip = (packageOption: App.OfferPackageOptionView) => {
      if (currentTrip && checkIn) {
        const checkOut = moment(checkIn).add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT)
        const payload = createLEHotelItemPayload({
          offer,
          pkg: packageOption.package,
          startDate: checkIn,
          endDate: checkOut,
          occupancies: rooms.map(room => ({
            adults: room.adults,
            teenagers: room.teenagers,
            children: room.children,
            infants: room.infants,
            childrenAge: room.childrenAge,
          })),
          currentRegionCode,
          currencyCode: currentCurrency,
          price: packageOption.hotelTotal,
          value: packageOption.hotelValue + packageOption.propertyFees,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }
    }

    const selectedLoyaltyUpgrade = getSelectedLoyaltyUpgrade(
      packageOption,
      availablePackageOptions,
      loyaltyTargetRoomUpgradesSelected,
    )

    if (selectedLoyaltyUpgrade) {
      fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, selectedLoyaltyUpgrade.packageOptionView.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
      generateCart({
        packageOption,
        flights,
        loyaltyAppliedUpgradeType: selectedLoyaltyUpgrade.upgradeType,
        basePackageId: packageOption.package.id,
      })
      saveToCurrentTrip(selectedLoyaltyUpgrade.packageOptionView)
      return
    }

    let upgradeAvailable: App.OfferPackageOptionView | undefined
    if (
      upgradeEnabled &&
      currentFunnelFeatures.roomUpgrade &&
      isEnabledForFeature(config.PACKAGE_UPGRADE_MODAL_ENABLED_LIST, offer.type) &&
      availablePackageOptions.length > 0 &&
      checkIn
    ) {
      if (isPackageUpgradeLibEnabled && isFlashHotelOffer(offer)) {
        // Mapping to lib-rooms-upgrade types
        const serverOffer = mapClientOfferToServerOffer(offer, packageOptions)
        const serverPackage = mapClientPackageToServerPackage(packageOption.package)
        const occupancies = mapRoomOccupantsToServerOccuppancies(rooms)
        const avaiableRoomsByPackage = getAvailableRoomsByPackage(packageOptions)
        const result = getUpgradeablePackageLib(
          serverOffer,
          serverPackage,
          occupancies,
          avaiableRoomsByPackage,
        )
        // Find the original package option that corresponds to the upgradable package
        upgradeAvailable = result ?
            availablePackageOptions.find(option => option.package.uniqueKey === result.package.uniqueKey) :
          undefined
      } else {
        upgradeAvailable = getUpgradeablePackage(
          availablePackageOptions,
          packageOption,
          moment(checkIn),
          isLpcHotelOffer(offer),
          offer.property.timezoneOffset,
        )
      }
    }

    if (upgradeAvailable && !isInternationalCheckoutFlowTreatment) {
      trackOfferPageEvent('UPGRADE_MODAL_OPENED')
      trackClientEvent({
        subject: 'upgrade_modal',
        action: 'opened',
        category: 'logging',
        type: 'nonInteraction',
      })
      showModal<App.OfferPackageOptionView | undefined>(<PackageUpgradeModal
        upgradePackageView={upgradeAvailable}
        basePackageView={packageOption}
        hasFlights={flights}
        offer={offer}
        checkIn={checkIn}
      />,
      ).then((resolvedPackageOption?: App.OfferPackageOptionView) => {
        if (resolvedPackageOption) {
          fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, resolvedPackageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
          generateCart({
            packageOption: resolvedPackageOption,
            flights,
            upgradePackageOption: upgradeAvailable,
            originalPackageOption: packageOption,
          })
          saveToCurrentTrip(resolvedPackageOption)
        }
      })
      return
    }

    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, packageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
    generateCart({ packageOption, flights })
    saveToCurrentTrip(packageOption)
  }, [offerPageDispatch, modalElementContext, availablePackageOptions, loyaltyTargetRoomUpgradesSelected, offer, upgradeEnabled, checkIn, currentCurrency, rooms, generateCart, currentTrip, currentRegionCode, createTripItems, isPackageUpgradeLibEnabled, packageOptions, trackOfferPageEvent, showModal, dispatch, isInternationalCheckoutFlowTreatment])

  const invalidRoomSelection = !hasSelectedRooms(rooms) || isRoomsInvalid(rooms)

  const onBookNowClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    const isFlightBuyType = e.currentTarget.dataset.buytype === 'flights'
    const shouldPrefetch = canPrefetchInFlow && !isFlightBuyType && !invalidRoomSelection && !!checkIn && !!packageView

    if (prefetchEnabled && shouldPrefetch) {
      const prefetchFilters = buildPrefetchTopOfferFilters({
        currencyCode: currentCurrency,
        offer,
        checkIn,
        checkOut,
        durationDays: duration,
        rooms,
        categories,
        geoCoords,
        oneClickTransferEnabled,
      })

      prefetchFilters.forEach((f: ExperienceListFilters) => {
        if (isExperienceListStale(experienceLists, f)) {
          dispatch(fetchExperienceList(f))
        }
      })
      triggerExperiencesAnalytics()
    }
    if (!latestPackageView) return
    onSubmit?.(e)

    if (invalidRoomSelection) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageKey: latestPackageView.package.uniqueKey,
      })
      return
    }

    // Open calendar if no checkIn, or for charter bundle if checkIn doesn't match available charter dates
    if (!checkIn || !isCheckInMatchingCharterDate) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageKey: latestPackageView.package.uniqueKey,
      })

      return
    }

    if (!isLoggedIn && !offer.walledGarden) {
      e.preventDefault()

      if (!isMobile) offerPageDispatch({ type: OfferPageActions.closeFilter })

      const hotelOfferDetails: App.AccountModalOfferDetails | undefined = latestPackageView && {
        title: offer.property?.name || latestPackageView.package.name,
        duration: latestPackageView.package.duration,
        value: isFlightBuyType ? latestPackageView.hotelPrice + latestPackageView.flightPrice : latestPackageView.hotelPrice,
        imageId: latestPackageView.package.roomType?.images[0].id,
        imageTitle: offer.property?.name || latestPackageView.package.name,
        discountPercentage: isFlightBuyType ? 0 : latestPackageView.hotelDiscountPercent,
        isBundledWithFlights: isFlightBuyType,
        offer,
        packageView: latestPackageView,
      }
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
        initialMode="login"
        hotelOfferDetails={hotelOfferDetails}
      />)

      if (result.loggedIn) {
        await onBookNow(latestPackageView, isFlightBuyType)
      }
      return
    }

    await onBookNow(latestPackageView, isFlightBuyType)
  }, [triggerExperiencesAnalytics, canPrefetchInFlow, invalidRoomSelection, checkIn, checkOut, duration, packageView, prefetchEnabled, latestPackageView, onSubmit, isLoggedIn, offer, onBookNow, currentCurrency, experienceLists, dispatch, modalElementContext, offerPageDispatch, isMobile, showModal, rooms, categories, geoCoords, oneClickTransferEnabled, isCheckInMatchingCharterDate])

  return (
    <Group direction="vertical" gap={4}>
      <TextButton {...rest} onClick={onBookNowClick} disabled={disabled || experiencesPassportFetching || awaitingCharterBundleData || insufficientCharterSeats}>
        {children}
      </TextButton>
      {insufficientCharterSeats && (
        <BodyText variant="small" colour="critical">
          {intl.formatMessage({
            id: 'c2bH-Zs44vaIoIqgSTYi',
            defaultMessage: 'Selected flight class is not available for your dates',
            description: 'Error message when charter flight seats are insufficient for selected passengers',
          })}
        </BodyText>
      )}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  airport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  isLoggedIn: isLoggedInSelector(appState),
  offer: state.offer,
  packageOptions: getDurationPackageOptions(state, appState),
  availablePackageOptions: getAvailableDurationPackageOptions(state, appState),
  rooms: ownProps.overrideRooms ?? state.rooms,
  tripPlannerTripId: appState.tripPlanner.currentTripId,
  loyaltyTargetRoomUpgradesSelected: state.loyaltyTargetStayUpgradesSelected,
  isPackageUpgradeLibEnabled: appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croPackageUpgradeLibEnabled],
  charterBundleRoute: state.charterBundleRoute,
  charterBundleEnabled: state.charterBundleEnabled,
  fareClass: state.fareClass,
  bypassRegionFilter: !!appState.marketing.bypassRegionSettingId,
  bypassRegionSettingId: appState.marketing.bypassRegionSettingId,
}), OfferCheckoutButton)
